import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import { ARTICLES, getArticle } from "@/lib/articles";
import { hero, articleImage } from "@/lib/images";
import PageShell from "@/components/PageShell";
import Breadcrumbs from "@/components/Breadcrumbs";
import ImageCard from "@/components/ImageCard";
import Seo, { faqSchema, breadcrumbSchema } from "@/components/Seo";

const ORIGIN = "https://bookandsmiles.com";

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const article = getArticle(slug);
  const a = article ? article[lang] : null;

  const crumbs = useMemo(
    () =>
      a
        ? [
            { label: lang === "fr" ? "Accueil" : "Home", to: "/" },
            { label: "Insights", to: "/journal" },
            { label: a.title, to: `/journal/${slug}` },
          ]
        : [],
    [a, lang, slug]
  );

  const schema = useMemo(() => {
    if (!a || !article) return null;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: a.title,
        description: a.seoDesc,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: lang,
        author: { "@type": "Person", name: "Michel Zollo", "@id": `${ORIGIN}/#michel` },
        publisher: { "@id": `${ORIGIN}/#business` },
        mainEntityOfPage: `${ORIGIN}/journal/${article.slug}`,
      },
      faqSchema(a.faqs),
      breadcrumbSchema(crumbs),
    ];
  }, [a, article, lang, crumbs]);

  if (!article) return <Navigate to="/journal" replace />;

  const idx = ARTICLES.findIndex((x) => x.slug === slug);
  const related = [1, 2, 3].map((n) => ARTICLES[(idx + n) % ARTICLES.length]);

  return (
    <PageShell>
      <Seo title={a.seoTitle} description={a.seoDesc} schema={schema} />

      <article>
        <header data-testid="article-hero" className="relative flex items-end overflow-hidden bg-slate-900 min-h-[70vh]">
          <img src={hero(articleImage(article.slug))} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/55 to-slate-950/30" />
          <div className="relative w-full max-w-4xl mx-auto px-6 lg:px-10 pt-40 pb-16 sm:pb-20">
            <Breadcrumbs items={crumbs.slice(0, 2)} light />
            <p className="eyebrow mt-8 !text-stone-300 fade-up">
              {a.category} · {a.readTime}
            </p>
            <h1 data-testid="article-title" className="font-serif mt-6 text-4xl sm:text-5xl lg:text-6xl font-light text-stone-50 leading-[1.15] sm:leading-[1.08] fade-up fade-up-delay-1">
              {a.title}
            </h1>
          </div>
        </header>

        <div data-testid="article-answer" className="px-6 lg:px-10 pt-16">
          <div className="max-w-3xl mx-auto border-l-2 border-stone-900 pl-6 py-2">
            <p className="text-stone-700 text-base md:text-lg leading-relaxed">{a.answer}</p>
          </div>
        </div>

        <div data-testid="article-body" className="px-6 lg:px-10 pt-16 pb-20">
          <div className="max-w-3xl mx-auto space-y-14">
            {a.sections.map((s) => (
              <section key={s.h2}>
                <h2 className="font-serif text-2xl sm:text-3xl text-stone-900 leading-snug">{s.h2}</h2>
                <div className="mt-5 space-y-5">
                  {s.paras.map((p) => (
                    <p key={p.slice(0, 40)} className="text-stone-600 leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
                {s.list && (
                  <ul className="mt-6 space-y-3">
                    {s.list.map((li) => (
                      <li key={li} className="flex items-start gap-3 text-stone-700 leading-relaxed">
                        <span className="mt-2.5 w-1.5 h-1.5 bg-stone-900 shrink-0" />
                        {li}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>

        <section data-testid="article-faq" className="bg-stone-100 border-y border-stone-200 py-16 sm:py-20 px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <p className="eyebrow">FAQ</p>
            <div className="mt-8 space-y-8">
              {a.faqs.map((f) => (
                <div key={f.q} className="border-b border-stone-200 pb-6">
                  <h3 className="font-medium text-stone-900">{f.q}</h3>
                  <p className="mt-2 text-stone-600 leading-relaxed text-sm">{f.a}</p>
                </div>
              ))}
            </div>
            <Link
              data-testid="article-cta"
              to={a.ctaTo}
              className="mt-12 inline-flex items-center gap-3 bg-slate-900 text-stone-50 px-8 py-4 text-sm font-medium hover:bg-slate-800 transition-colors"
            >
              {a.ctaLabel}
              <ArrowRight size={16} />
            </Link>
          </div>
        </section>

        <section data-testid="article-related" className="py-14 sm:py-24 px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <p className="eyebrow">{t.journal.eyebrow}</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <ImageCard
                  key={r.slug}
                  testId={`related-${r.slug}`}
                  to={`/journal/${r.slug}`}
                  image={articleImage(r.slug)}
                  eyebrow={r[lang].category}
                  title={r[lang].title}
                  cta={lang === "fr" ? "Lire" : "Read"}
                  aspect="aspect-[4/3]"
                />
              ))}
            </div>
          </div>
        </section>
      </article>
    </PageShell>
  );
}
